import { useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Radio,
  Space,
  Tag,
  Typography,
} from "antd";
import { recordDecision } from "../../lib/api";
import type { IntakeWorkflow } from "../intakeDetails/types";

interface Props {
  workflow: IntakeWorkflow | null;
  onRecorded: () => void;
}

export default function DecisionPanel({ workflow, onRecorded }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  if (!workflow) {
    return <Alert type="info" message="Create the workflow first." showIcon />;
  }

  const decision = workflow.decision;

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await recordDecision({
        workflowId: workflow.id,
        decision: values.decision,
        rationale: values.rationale.trim(),
      });
      message.success(`Recorded: ${values.decision}`);
      onRecorded();
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (decision) {
    return (
      <Card title="Decision recorded">
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Tag
            color={
              decision.decision === "approved"
                ? "success"
                : decision.decision === "declined"
                  ? "error"
                  : "warning"
            }
            style={{ fontSize: 16, padding: "4px 12px" }}
          >
            {decision.decision.toUpperCase()}
          </Tag>
          <Typography.Paragraph>{decision.rationale}</Typography.Paragraph>
          <Typography.Text type="secondary">
            Recorded {decision.decidedAt ? new Date(decision.decidedAt).toLocaleString() : ""}
            {decision.decidedByEmail ? ` by ${decision.decidedByEmail}` : ""}
          </Typography.Text>
        </Space>
      </Card>
    );
  }

  return (
    <Card title="Record approval decision">
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Decision"
          name="decision"
          rules={[{ required: true, message: "Pick a decision" }]}
        >
          <Radio.Group>
            <Radio value="approved" aria-label="Approve project">Approved</Radio>
            <Radio value="declined" aria-label="Decline project">Declined</Radio>
            <Radio value="deferred" aria-label="Defer project">Deferred</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          label="Rationale"
          name="rationale"
          rules={[{ required: true, message: "Provide a rationale" }]}
        >
          <Input.TextArea
            autoSize={{ minRows: 4 }}
            placeholder="Document the reasoning for the decision (will appear in the audit trail)."
            aria-label="Decision rationale"
          />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={submitting}>
          Record decision
        </Button>
      </Form>
    </Card>
  );
}
